import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const mins = (s) => `${Math.round((s || 0) / 60)} menit`;

export default function TrainingControl() {
  const [staffList, setStaffList] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [speed, setSpeed] = useState("REAL");
  const [custom, setCustom] = useState(1);
  const [busy, setBusy] = useState(false);

  const loadSessions = useCallback(() => { api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/users?role=staff").then((r) => setStaffList(r.data)).catch(() => {});
    api.get("/datasets").then((r) => setDatasets(r.data)).catch(() => {});
    loadSessions();
    const t = setInterval(loadSessions, 5000);
    return () => clearInterval(t);
  }, [loadSessions]);

  const ds = datasets.find((d) => d.id === datasetId);

  const start = async () => {
    if (!staffId) return toast.error("Pilih staff dulu");
    if (!datasetId) return toast.error("Pilih data training dulu");
    setBusy(true);
    try {
      const { data: sess } = await api.post("/sessions", {
        staff_id: staffId, dataset_id: datasetId, speed, custom_factor: Number(custom),
      });
      const { data } = await api.post(`/sessions/${sess.id}/start`);
      toast.success(`Training dimulai — ${data.generated} form akan masuk ke staff`);
      loadSessions();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ctrl = async (sid, action) => {
    try { await api.post(`/sessions/${sid}/${action}`); toast.success(`Session ${action}`); loadSessions(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const SPEEDS = [["REAL", "WAKTU ASLI"], ["2X", "2X LEBIH CEPAT"], ["3X", "3X LEBIH CEPAT"], ["CUSTOM", "CUSTOM"]];

  return (
    <div data-testid="training-control-page">
      <div className="section-head">Training Control — Replay Dataset</div>

      <div style={{ maxWidth: 560 }}>
        <div className="form-row"><label>Staff yang ditraining</label>
          <select className="form-select" value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="tc-staff">
            <option value="">-- pilih staff --</option>
            {staffList.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
          </select></div>

        <div className="form-row"><label>Pilih Data Training</label>
          <select className="form-select" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} data-testid="tc-dataset">
            <option value="">-- pilih dataset --</option>
            {datasets.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></div>

        {ds && (
          <div className="report-grid" style={{ marginBottom: 12, maxWidth: 420 }} data-testid="tc-dataset-info">
            <div className="rk">Form</div><div>{ds.form_count}</div>
            <div className="rk">Deposit</div><div>{ds.deposit_count}</div>
            <div className="rk">Withdraw</div><div>{ds.withdraw_count}</div>
            <div className="rk">Durasi data asli</div><div>{mins(ds.original_duration_seconds)}</div>
          </div>
        )}

        <div className="form-row" style={{ alignItems: "flex-start" }}><label>Kecepatan Training</label>
          <div data-testid="tc-speed">
            {SPEEDS.map(([val, lbl]) => (
              <label key={val} style={{ display: "block", fontWeight: "normal", marginBottom: 3 }}>
                <input type="radio" name="speed" value={val} checked={speed === val} onChange={() => setSpeed(val)} data-testid={`tc-speed-${val}`} /> {lbl}
              </label>
            ))}
            {speed === "CUSTOM" && (
              <div style={{ marginTop: 4 }}>
                <input className="form-input" type="number" step="0.5" min="0.5" style={{ minWidth: 80 }}
                  value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="tc-custom" /> x lebih cepat
              </div>
            )}
          </div></div>

        <div className="action-bar" style={{ background: "transparent", border: "none", textAlign: "left", padding: 0 }}>
          <button className="btn btn-accept" onClick={start} disabled={busy} data-testid="tc-start">MULAI TRAINING</button>
        </div>
      </div>

      <div className="section-head" style={{ marginTop: 24 }}>Sessions</div>
      <table className="panel-table" data-testid="sessions-table">
        <thead><tr><th>Staff</th><th>Dataset</th><th>Speed</th><th>Status</th><th>Control</th></tr></thead>
        <tbody>
          {sessions.length === 0 && <tr className="empty-row"><td colSpan={5}>Belum ada session</td></tr>}
          {sessions.map((s) => (
            <tr key={s.id} data-testid={`session-row-${s.id}`}>
              <td>{s.staff_name}</td>
              <td>{s.dataset_name || s.scenario}</td>
              <td>{s.speed || "-"}</td>
              <td><span className={`status-pill status-${s.status}`}>{s.status}</span></td>
              <td className="nowrap">
                {s.status === "RUNNING" && <button className="btn btn-gray" onClick={() => ctrl(s.id, "pause")} data-testid={`pause-${s.id}`}>PAUSE</button>}
                {s.status === "PAUSED" && <button className="btn btn-gray" onClick={() => ctrl(s.id, "resume")} data-testid={`resume-${s.id}`}>RESUME</button>}
                {(s.status === "RUNNING" || s.status === "PAUSED") && <button className="btn btn-reject" style={{ marginLeft: 4 }} onClick={() => ctrl(s.id, "stop")} data-testid={`stop-${s.id}`}>STOP</button>}
                <button className="btn btn-gray" style={{ marginLeft: 4 }} onClick={() => ctrl(s.id, "reset")} data-testid={`reset-${s.id}`}>RESET</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
